import React, { useEffect, useRef, useState } from 'react';
import Autocomplete from 'react-google-autocomplete';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  VStack,
  Flex,
  Text,
  Wrap,
  Input,
  Button,
  HStack,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  setCurrentStep,
  setStep3,
  useAppDispatch,
  useAppSelector,
  useCreateNewScheduleMutation,
  useCreateMultipleSchedulesMutation,
} from '@spartalabs/pdc-core';
import { Card, SimpleInput, Unauthorized } from 'components';
import { addDays } from 'date-fns';
import { useCustomToast, useAnalyticsEvent } from 'hooks';
import { DelayAnimation } from 'shared/DelayAnimation';
import { GA_NEW_SCHEDULE_LABEL } from 'utils/constants';
import * as z from 'zod';
import { ConfirmModal } from '../Modals/ConfirmModal';
import { FileModal } from '../Modals/FileModal';
import { NewScheduleHeader } from '../NewScheduleHeader';
import './styles.css';

const ScheduleFormSchema = z.object({
  scheduleDate: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' }),
  scheduleTime: z.string().nonempty({ message: 'Campo obrigatório' }),
  address: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' }),
  name: z.string().nonempty({ message: 'Campo obrigatório' }),
  email: z
    .string()
    .nonempty('Campo obrigatório')
    .email('Email inválido')
    .optional(),
  phone: z
    .string({
      required_error: 'Campo obrigatório',
    })
    .nonempty({ message: 'Campo obrigatório' })
    .refine(value => !value.includes('_'), 'Celular inválido'),
});

type Step3FormSchema = z.infer<typeof ScheduleFormSchema>;

export const ScheduleForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { step1, step2, step3, multiple } = useAppSelector(
    state => state.scheduleSlicer,
  );

  const [fetchForm, { isLoading }] = useCreateNewScheduleMutation();
  const [createMultiple, { data: multipleData, isLoading: MultiLoading }] =
    useCreateMultipleSchedulesMutation();

  const { handleToast } = useCustomToast();

  const [showModal, setShowModal] = useState(false);

  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_NEW_SCHEDULE_LABEL,
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<Step3FormSchema>({
    resolver: zodResolver(ScheduleFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const responseId = useRef<string>('');

  const values = {
    time: watch('scheduleTime'),
    date: watch('scheduleDate'),
  };

  const handleClose = () => {
    setShowModal(false);
    navigate('/agendamentos');
  };

  const onSubmit = async (data: Step3FormSchema) => {
    try {
      if (state?.isMultiple && multiple?.plates) {
        await createMultiple({
          plates: multiple?.plates,
          body: {
            ...data,
            personId: multiple?.personID,
          },
        }).unwrap();

        setShowModal(true);
      } else {
        analyticsEvent(`${GA_NEW_SCHEDULE_LABEL} Finalizado`);
        const id = await fetchForm(
          Object.assign({}, step1, step2, { ...data, email: data.email || '' }),
        ).unwrap();
        responseId.current = id;
        setShowModal(true);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleToast({
        step: 4,
        action: 'tentar finalizar o agendamento',
        helpText: error?.data?.msn_errors?.[0] || '',
      });
    }
  };

  const handlePreviousStep = () => {
    dispatch(setStep3({ ...getValues(), email: getValues('email') || '' }));

    dispatch(setCurrentStep(2));

    navigate(
      state?.isMultiple
        ? '/novo-agendamento/lote'
        : '/novo-agendamento/servicos',
    );
  };

  useEffect(() => {
    if (addDays(new Date(), 1) > new Date(`${values?.date} ${values?.time}`)) {
      setError('scheduleTime', {
        type: 'custom',
        message: 'Os agendamentos são marcados com 24 horas de antecedência.',
      });
    } else clearErrors('scheduleTime');
  }, [values?.time, values?.date]);

  if (!permissions?.includes('createSchedule')) return <Unauthorized />;
  return (
    <DelayAnimation>
      <NewScheduleHeader isMultiple={state?.isMultiple} step={3} />
      {state?.isMultiple ? (
        <FileModal
          isFinal
          file={multipleData}
          isOpen={showModal}
          total={multiple?.sentVehicles}
          onClose={handleClose}
        />
      ) : (
        <ConfirmModal
          isOpen={showModal}
          onClose={handleClose}
          plate={step1.plate || 'Não Definida'}
          date={watch('scheduleDate')}
          id={responseId.current}
        />
      )}

      <VStack
        w="100%"
        display="flex"
        align="center"
        justify="center"
        pt="44px"
        pb="44px"
      >
        <Flex flexDir="column" align="center" justify="center" mb="32px">
          <Text fontSize="18px" color="text.100">
            Informe os dados para agendamento
          </Text>
          <Text fontSize="14px" color="text.300" mt="3px">
            Insira os dados abaixo
          </Text>
        </Flex>

        <Card maxW="400px">
          <Flex flexDir="column" gap="24px" mt="32px">
            <Wrap>
              <Text as="label" fontSize="14px" color="text.300">
                *Endereço de referência sugerido
              </Text>
              <Autocomplete
                // move to .env
                apiKey="AIzaSyAnizZUvtAsW4bb2uO-crScROEzu4md5vU"
                placeholder="Digite a endereço de referência sugerido"
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '17px 20px',
                  border: '1px solid gray',
                  color: '#000000',
                  background: 'transparent',
                  borderRadius: '4px',
                  outline: 'transparent',
                }}
                onPlaceSelected={place => {
                  const { formatted_address } = place;
                  setValue('address', formatted_address);
                }}
                options={{
                  types: ['address'],
                  componentRestrictions: { country: 'br' },
                }}
              />

              {errors.address && (
                <Text alignSelf="start" pl="8px" color="red.200" fontSize="sm">
                  {errors.address?.message}
                </Text>
              )}
            </Wrap>

            <VStack flexWrap="wrap" alignItems="flex-start">
              <Text as="label" fontSize="14px" color="text.300">
                *Data
              </Text>

              <Input
                required
                type="date"
                {...register('scheduleDate')}
                placeholder="Digite data e hora sugerida"
                defaultValue={step3?.scheduleDate || ''}
                maxLength={7}
                min={addDays(new Date(), 1).toISOString().split('T')[0]}
                bg="transparent"
                borderColor="grey.600"
                color="text.100"
                fontSize="16px"
                fontWeight="500"
                size="lg"
                css={css`
                  ::-webkit-calendar-picker-indicator {
                    filter: invert(100%) sepia(0%) saturate(3207%)
                      hue-rotate(130deg) brightness(95%) contrast(80%);
                    width: 100%;
                    position: absolute;
                    padding-left: 308px;
                  }
                `}
              />
              {errors.scheduleDate && (
                <Text alignSelf="start" pl="8px" color="red.200" fontSize="sm">
                  {errors.scheduleDate?.message}
                </Text>
              )}
            </VStack>

            <SimpleInput
              required
              aria-label="Horário sugerido"
              label="*Horário sugerido"
              labelColor="text.300"
              type="time"
              {...register('scheduleTime')}
              size="lg"
              placeholder="Digite nome do condutor"
              defaultValue={step3?.scheduleTime || ''}
              bg="transparent"
              borderColor="grey.600"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.scheduleTime?.message}
              css={css`
                ::-webkit-calendar-picker-indicator {
                  filter: invert(100%) sepia(0%) saturate(3207%)
                    hue-rotate(130deg) brightness(95%) contrast(80%);
                  width: 100%;
                  position: absolute;
                  padding-left: 298px;
                }
              `}
            />

            <SimpleInput
              _placeholder={{ color: 'grey.500' }}
              required
              label="*Nome / Condutor"
              labelColor="text.300"
              type="email"
              {...register('name')}
              size="lg"
              placeholder="Digite nome do condutor"
              defaultValue={step3?.name || ''}
              bg="transparent"
              borderColor="grey.600"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.name?.message}
            />

            <SimpleInput
              _placeholder={{ color: 'grey.500' }}
              required
              label="*E-mail"
              labelColor="text.300"
              type="email"
              {...register('email')}
              size="lg"
              placeholder="Digite o e-mail"
              defaultValue={step3?.email || ''}
              bg="transparent"
              borderColor="grey.600"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.email?.message}
            />

            <SimpleInput
              _placeholder={{ color: 'grey.500' }}
              aria-label="adicione um telefone"
              required
              label="*Celular"
              labelColor="text.300"
              type="phone"
              {...register('phone')}
              id="phone"
              size="lg"
              placeholder="Digite o telefone do condutor"
              defaultValue={step3?.phone || ''}
              bg="transparent"
              borderColor="grey.600"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              error={errors.phone?.message}
            />
          </Flex>
          <HStack mt="32px" w="100%" justifyContent="flex-end">
            <Button variant="outline" onClick={handlePreviousStep}>
              Voltar
            </Button>

            <Button
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading || MultiLoading}
            >
              Finalizar
            </Button>
          </HStack>
        </Card>
      </VStack>
    </DelayAnimation>
  );
};
